<template>
  <div class="terminal-page page-container">
    <div class="terminal-toolbar">
      <el-input v-model.trim="keyword" clearable placeholder="搜索省区 / 医院 / 科室 / 医生" prefix-icon="el-icon-search" @keyup.enter.native="search" @clear="clearSearch" />
      <el-button icon="el-icon-search" @click="search">搜索</el-button>
      <el-button-group class="tree-actions"><el-button icon="el-icon-arrow-down" @click="expandAll">全部展开</el-button><el-button icon="el-icon-arrow-right" @click="collapseAll">全部折叠</el-button></el-button-group>
      <el-button class="primary-action" icon="el-icon-plus" @click="openCreate">新增终端</el-button>
      <el-button class="ai-action" :loading="profileRefreshState.running" :disabled="profileRefreshState.running" @click="completeProfiles">🤖 AI 画像补全</el-button>
      <span v-if="profileRefreshState.running" class="ai-progress">AI画像更新中 {{ profileRefreshState.current }} / {{ profileRefreshState.total }}</span>
      <span v-else class="ai-note">AI-08 每次进入页面自动串行更新</span>
    </div>

    <section class="terminal-tree panel" v-loading="loading">
      <div class="tree-heading">
        <div><strong>省区 → 医院 → 科室 → 医生</strong><span>（四级结构）</span></div>
        <div class="summary">{{ tree.hospital_count || 0 }} 医院 / {{ tree.department_count || 0 }} 科室 / {{ tree.doctor_count || 0 }} 医生</div>
      </div>
      <el-empty v-if="!loading && !regions.length" :description="keyword ? '未找到匹配的省区、医院、科室或医生' : '暂无终端数据'" />

      <div v-for="region in regions" :key="region.sjid" class="region-block">
        <div class="tree-card region-card node-toggle" role="button" :aria-expanded="isExpanded('region', region.sjid)" @click="toggle('region', region.sjid)">
          <i :class="arrowClass('region', region.sjid)" class="fold-arrow" />
          <span class="node-icon">📌</span>
          <div class="node-main"><strong :title="region.region_name">{{ region.region_name }}</strong><el-tag size="mini">{{ originLabel(region.data_origin) }}</el-tag><small :title="'省区经理：' + (region.manager_name || '未设置') + ' · 区域代码 ' + region.region_code">省区经理：{{ region.manager_name || '未设置' }} · 区域代码 {{ region.region_code }}</small></div>
          <div class="node-count">{{ region.hospital_count }} 医院 / {{ region.department_count }} 科室 / {{ region.doctor_count }} 医生</div>
        </div>

        <transition-group name="tree-fold" tag="div">
        <div v-for="hospital in (isExpanded('region', region.sjid) ? region.hospitals : [])" :key="'hospital-' + hospital.sjid" class="hospital-branch branch">
          <div class="tree-card hospital-card node-toggle" role="button" :aria-expanded="isExpanded('hospital', hospital.sjid)" @click="toggle('hospital', hospital.sjid)">
            <i :class="arrowClass('hospital', hospital.sjid)" class="fold-arrow" />
            <span class="node-icon">🏥</span>
            <div class="node-main"><strong :title="hospital.hospital_name">{{ hospital.hospital_name }}</strong><el-tag v-if="hospital.hospital_level" size="mini">{{ hospital.hospital_level }}</el-tag><el-tag size="mini">{{ originLabel(hospital.data_origin) }}</el-tag><small :title="(hospital.address || '地址待完善') + ' · ' + hospital.hospital_code + (hospital.alias_names ? ' · 简称：' + hospital.alias_names : '')">{{ hospital.address || '地址待完善' }} · {{ hospital.hospital_code }}<template v-if="hospital.alias_names"> · 简称：{{ hospital.alias_names }}</template></small></div>
            <div class="node-count">{{ hospital.department_count }} 科室 / {{ hospital.doctor_count }} 医生</div>
            <el-button type="text" class="node-edit" @click.stop="openHospitalEdit(hospital, region.sjid)">编辑</el-button>
          </div>

          <transition-group name="tree-fold" tag="div">
          <div v-for="department in (isExpanded('hospital', hospital.sjid) ? hospital.departments : [])" :key="'department-' + department.sjid" class="department-branch branch">
            <div class="tree-card department-card node-toggle" role="button" :aria-expanded="isExpanded('department', department.sjid)" @click="toggle('department', department.sjid)">
              <i :class="arrowClass('department', department.sjid)" class="fold-arrow" />
              <span class="node-icon">🏷️</span>
              <div class="node-main"><strong :title="department.department_name">{{ department.department_name }}</strong><el-tag size="mini">{{ originLabel(department.data_origin) }}</el-tag><small :title="'科室负责人：' + (department.responsible_name || '未设置') + (department.responsible_mobile ? ' · ' + maskMobile(department.responsible_mobile) : '')">科室负责人：{{ department.responsible_name || '未设置' }}<template v-if="department.responsible_mobile"> · {{ maskMobile(department.responsible_mobile) }}</template></small></div>
              <div class="node-count">{{ department.doctor_count }} 医生</div>
              <div class="node-actions"><el-button type="text" @click.stop="openDoctorCreate(department.sjid)">新增医生</el-button><el-button type="text" @click.stop="openDepartmentEdit(department, hospital.sjid)">编辑</el-button></div>
            </div>

            <transition-group name="tree-fold" tag="div">
            <div v-for="doctor in (isExpanded('department', department.sjid) ? department.doctors : [])" :key="'doctor-' + doctor.sjid" class="doctor-branch branch">
              <div class="tree-card doctor-card">
                <span class="leaf-indent" />
                <span class="node-icon">👨‍⚕️</span>
                <div class="node-main">
                  <strong :title="doctor.doctor_name">{{ doctor.doctor_name }}</strong><el-tag size="mini">{{ originLabel(doctor.data_origin) }}</el-tag><el-tag v-if="doctor.customer_level" class="level-tag" size="mini">{{ doctor.customer_level }}级</el-tag><el-tag v-if="doctor.ai_profile_enabled" class="ai-tag" size="mini">{{ doctor.ai_profile_tags ? 'AI画像' : '画像待生成' }}</el-tag>
                  <small :title="(doctor.title_name || '职称待完善') + (doctor.ai_profile_enabled && doctor.ai_profile_tags ? ' · AI标签：' + doctor.ai_profile_tags : '')">{{ doctor.title_name || '职称待完善' }}<template v-if="doctor.ai_profile_enabled && doctor.ai_profile_tags"> · AI标签：{{ doctor.ai_profile_tags }}</template></small>
                </div>
                <div class="node-count">{{ doctor.visit_count }} 次拜访</div>
                <el-button type="text" class="doctor-edit" @click="openDoctorEdit(doctor, department.sjid)">编辑</el-button>
              </div>
            </div>
            </transition-group>
          </div>
          </transition-group>
        </div>
        </transition-group>
      </div>
    </section>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="680px" custom-class="business-edit-dialog" :close-on-click-modal="false" @closed="resetForm">
      <el-form ref="terminalForm" class="business-form" :model="form" :rules="rules" label-position="top">
        <el-form-item label="层级" prop="level">
          <el-select v-model="form.level" :disabled="Boolean(form.sjid)" style="width:100%" @change="levelChanged">
            <el-option label="医院" value="HOSPITAL" /><el-option label="科室" value="DEPARTMENT" /><el-option label="医生" value="DOCTOR" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称" prop="name"><el-input v-model.trim="form.name" maxlength="150" show-word-limit :placeholder="namePlaceholder" /></el-form-item>
        <el-form-item :label="parentLabel" prop="parent_id">
          <el-select v-model="form.parent_id" filterable style="width:100%" :placeholder="'请选择' + parentLabel">
            <el-option v-for="item in parentOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>

        <template v-if="form.level === 'HOSPITAL'">
          <el-row :gutter="16"><el-col :span="12"><el-form-item label="医院编码"><el-input v-model.trim="form.code" placeholder="留空自动生成" /></el-form-item></el-col><el-col :span="12"><el-form-item label="医院等级"><el-select v-model="form.hospital_level" clearable style="width:100%"><el-option label="三甲" value="三甲" /><el-option label="三乙" value="三乙" /><el-option label="二甲" value="二甲" /></el-select></el-form-item></el-col></el-row>
          <el-form-item label="医院地址"><el-input v-model.trim="form.address" maxlength="300" /></el-form-item>
          <el-form-item label="医院简称/别名"><el-input v-model.trim="form.hospital_aliases" maxlength="500" placeholder="多个别名用逗号分隔，如：市一院，上海一院" /></el-form-item>
        </template>
        <template v-else-if="form.level === 'DEPARTMENT'">
          <el-row :gutter="16"><el-col :span="12"><el-form-item label="科室编码"><el-input v-model.trim="form.code" placeholder="留空自动生成" /></el-form-item></el-col><el-col :span="12"><el-form-item label="科室负责人"><el-input v-model.trim="form.responsible_name" /></el-form-item></el-col></el-row>
          <el-form-item label="负责人电话"><el-input v-model.trim="form.responsible_mobile" maxlength="30" /></el-form-item>
        </template>
        <template v-else>
          <el-row :gutter="16"><el-col :span="8"><el-form-item label="职称"><el-input v-model.trim="form.title_name" placeholder="如：主任医师" /></el-form-item></el-col><el-col :span="8"><el-form-item label="客户等级"><el-select v-model="form.customer_level" clearable style="width:100%"><el-option v-for="level in ['A','B','C']" :key="level" :label="level + '级'" :value="level" /></el-select></el-form-item></el-col><el-col :span="8"><el-form-item label="备案状态"><el-select v-model="form.registration_status" style="width:100%"><el-option label="已备案" value="REGISTERED" /><el-option label="未备案" value="UNREGISTERED" /></el-select></el-form-item></el-col></el-row>
          <el-form-item label="业务兴趣标签（非AI）"><el-input v-model.trim="form.interest_tags" placeholder="如：指南更新/联合用药" /></el-form-item>
          <el-form-item label="AI 画像补全"><el-select v-model="form.ai_profile_enabled" style="width:100%"><el-option label="开启（AI 自动聚合拜访历史生成画像）" :value="true" /><el-option label="关闭" :value="false" /></el-select></el-form-item>
        </template>
      </el-form>
      <span slot="footer" class="business-dialog-actions"><el-button @click="dialogVisible=false">取消</el-button><el-button class="primary-action" :loading="saving" @click="save">保存</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { terminalApi, aiApi } from '../services/api'

const emptyForm = () => ({ sjid: '', level: 'HOSPITAL', parent_id: '', name: '', code: '', hospital_level: '', address: '', hospital_aliases: '', responsible_name: '', responsible_mobile: '', title_name: '', customer_level: '', registration_status: 'REGISTERED', interest_tags: '', ai_profile_enabled: false })
const emptyRefreshState = () => ({ running: false, mode: '', total: 0, current: 0, success: 0, failed: 0, skipped: 0, cancelled: false })

export default {
  data: () => ({
    keyword: '', loading: false, saving: false, dialogVisible: false,
    tree: { regions: [], hospital_count: 0, department_count: 0, doctor_count: 0 }, form: emptyForm(),
    expanded: { region: {}, hospital: {}, department: {} }, beforeSearchExpanded: null, expansionInitialized: false,
    autoProfileRefreshStarted: false, profileRefreshDoctorIds: [], profileRefreshState: emptyRefreshState(), refreshedDoctorIds: {},
    rules: { level: [{ required: true, message: '请选择层级', trigger: 'change' }], name: [{ required: true, message: '请输入名称', trigger: 'blur' }], parent_id: [{ required: true, message: '请选择所属上级', trigger: 'change' }] }
  }),
  computed: {
    regions () { return this.tree.regions || [] },
    dialogTitle () {
      const label = { HOSPITAL: '医院', DEPARTMENT: '科室', DOCTOR: '医生' }[this.form.level] || '终端'
      return (this.form.sjid ? '编辑' : '新增') + label
    },
    parentLabel () { return this.form.level === 'HOSPITAL' ? '所属省区' : (this.form.level === 'DEPARTMENT' ? '所属医院' : '所属科室') },
    namePlaceholder () { return this.form.level === 'HOSPITAL' ? '医院名称' : (this.form.level === 'DEPARTMENT' ? '科室名称' : '医生姓名') },
    parentOptions () {
      if (this.form.level === 'HOSPITAL') return this.regions.map(r => ({ value: r.sjid, label: r.region_name }))
      const hospitals = []
      this.regions.forEach(r => (r.hospitals || []).forEach(h => hospitals.push({ region: r, hospital: h })))
      if (this.form.level === 'DEPARTMENT') return hospitals.map(x => ({ value: x.hospital.sjid, label: x.region.region_name + ' / ' + x.hospital.hospital_name }))
      const departments = []
      hospitals.forEach(x => (x.hospital.departments || []).forEach(d => departments.push({ value: d.sjid, label: x.hospital.hospital_name + ' / ' + d.department_name })))
      return departments
    }
  },
  created () { this.load({ autoRefresh: true }) },
  beforeDestroy () { this.profileRefreshState.cancelled = true },
  methods: {
    async load (options) {
      this.loading = true
      let startAutoRefresh = false
      try {
        this.tree = await terminalApi.tree(this.keyword)
        if (this.keyword) this.expandAll()
        else if (options && options.restore) { this.expanded = this.cloneExpanded(options.restore); this.expansionInitialized = true }
        else if (!this.expansionInitialized) this.applyDefaultExpansion()
        if (options && options.autoRefresh && !this.autoProfileRefreshStarted) {
          this.autoProfileRefreshStarted = true
          this.profileRefreshDoctorIds = this.collectEnabledDoctorIds()
          startAutoRefresh = true
        }
      }
      catch (e) { this.$message.error('数据加载失败，请稍后重试') }
      finally {
        this.loading = false
        if (startAutoRefresh) this.$nextTick(() => this.startDoctorProfileQueue('AUTO'))
      }
    },
    async search () {
      if (this.keyword && !this.beforeSearchExpanded) this.beforeSearchExpanded = this.cloneExpanded(this.expanded)
      if (!this.keyword) return this.clearSearch()
      await this.load()
    },
    async clearSearch () {
      const restore = this.beforeSearchExpanded
      this.beforeSearchExpanded = null
      this.keyword = ''
      await this.load({ restore })
    },
    cloneExpanded (value) { return JSON.parse(JSON.stringify(value || { region: {}, hospital: {}, department: {} })) },
    applyDefaultExpansion () {
      this.expanded = { region: {}, hospital: {}, department: {} }
      this.regions.forEach(region => this.$set(this.expanded.region, region.sjid, true))
      this.expansionInitialized = true
    },
    isExpanded (level, id) { return Boolean(this.expanded[level] && this.expanded[level][id]) },
    toggle (level, id) { this.$set(this.expanded[level], id, !this.isExpanded(level, id)) },
    arrowClass (level, id) { return this.isExpanded(level, id) ? 'el-icon-arrow-down' : 'el-icon-arrow-right' },
    expandAll () {
      this.expansionInitialized = true
      this.regions.forEach(region => {
        this.$set(this.expanded.region, region.sjid, true)
        ;(region.hospitals || []).forEach(hospital => {
          this.$set(this.expanded.hospital, hospital.sjid, true)
          ;(hospital.departments || []).forEach(department => this.$set(this.expanded.department, department.sjid, true))
        })
      })
    },
    collapseAll () { this.expansionInitialized = true; this.expanded = { region: {}, hospital: {}, department: {} } },
    openCreate () { this.form = emptyForm(); this.dialogVisible = true },
    openHospitalEdit (hospital, regionId) {
      this.form = Object.assign(emptyForm(), {
        sjid: hospital.sjid, level: 'HOSPITAL', parent_id: regionId, name: hospital.hospital_name,
        code: hospital.hospital_code || '', hospital_level: hospital.hospital_level || '', address: hospital.address || '',
        hospital_aliases: (hospital.alias_names || '').split(' / ').join('，')
      })
      this.dialogVisible = true
    },
    openDepartmentEdit (department, hospitalId) {
      this.form = Object.assign(emptyForm(), {
        sjid: department.sjid, level: 'DEPARTMENT', parent_id: hospitalId, name: department.department_name,
        code: department.department_code || '', responsible_name: department.responsible_name || '', responsible_mobile: department.responsible_mobile || ''
      })
      this.dialogVisible = true
    },
    openDoctorCreate (departmentId) {
      this.form = Object.assign(emptyForm(), { level: 'DOCTOR', parent_id: departmentId })
      this.dialogVisible = true
    },
    openDoctorEdit (doctor, departmentId) {
      this.form = Object.assign(emptyForm(), {
        sjid: doctor.sjid, level: 'DOCTOR', parent_id: departmentId, name: doctor.doctor_name,
        title_name: doctor.title_name || '', customer_level: doctor.customer_level || '', registration_status: doctor.registration_status || 'REGISTERED',
        interest_tags: doctor.interest_tags || '', ai_profile_enabled: Boolean(doctor.ai_profile_enabled)
      })
      this.dialogVisible = true
    },
    resetForm () { this.form = emptyForm(); if (this.$refs.terminalForm) this.$refs.terminalForm.clearValidate() },
    levelChanged () { this.form.parent_id = ''; this.$nextTick(() => this.$refs.terminalForm && this.$refs.terminalForm.clearValidate('parent_id')) },
    save () {
      this.$refs.terminalForm.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          const parentId = this.form.parent_id; const level = this.form.level
          const savedId = await terminalApi.save(this.form)
          if (level === 'DOCTOR') this.syncProfileRefreshDoctor(savedId, this.form.ai_profile_enabled)
          this.$message.success('终端数据保存成功'); this.dialogVisible = false; await this.load()
          this.expandCreatedParent(level, parentId)
        }
        catch (e) { this.$message.error(e.message) }
        finally { this.saving = false }
      })
    },
    async completeProfiles () {
      if (this.profileRefreshState.running) return this.$message.info('AI画像正在更新，请稍候')
      await this.startDoctorProfileQueue('MANUAL')
    },
    collectEnabledDoctorIds () {
      const ids = []; const seen = {}
      this.regions.forEach(region => (region.hospitals || []).forEach(hospital => (hospital.departments || []).forEach(department => (department.doctors || []).forEach(doctor => {
        const id = String(doctor.sjid || '')
        if (doctor.ai_profile_enabled === true && id && !seen[id]) { seen[id] = true; ids.push(id) }
      }))))
      return ids
    },
    syncProfileRefreshDoctor (doctorId, enabled) {
      const id = String(doctorId || '')
      if (!id) return
      this.profileRefreshDoctorIds = this.profileRefreshDoctorIds.filter(item => String(item) !== id)
      if (enabled) this.profileRefreshDoctorIds.push(id)
    },
    async startDoctorProfileQueue (mode) {
      if (this.profileRefreshState.running) return
      const doctorIds = this.profileRefreshDoctorIds.slice()
      this.profileRefreshState = Object.assign(emptyRefreshState(), { running: true, mode, total: doctorIds.length })
      this.refreshedDoctorIds = {}
      for (let i = 0; i < doctorIds.length; i++) {
        if (this.profileRefreshState.cancelled) break
        const doctorId = doctorIds[i]
        this.profileRefreshState.current = i + 1
        try {
          const result = await aiApi.refreshDoctorProfile(doctorId)
          if (result && result.refreshed) {
            this.profileRefreshState.success++
            this.$set(this.refreshedDoctorIds, doctorId, true)
            this.updateDoctorProfileInTree(result)
          } else this.profileRefreshState.skipped++
        } catch (e) { this.profileRefreshState.failed++ }
      }
      this.profileRefreshState.running = false
      if (!this.profileRefreshState.cancelled) {
        this.$message({ type: this.profileRefreshState.failed ? 'warning' : 'success', duration: 5000,
          message: 'AI画像更新完成：成功 ' + this.profileRefreshState.success + '，失败 ' + this.profileRefreshState.failed + '，跳过 ' + this.profileRefreshState.skipped })
      }
    },
    updateDoctorProfileInTree (result) {
      this.regions.forEach(region => (region.hospitals || []).forEach(hospital => (hospital.departments || []).forEach(department => (department.doctors || []).forEach(doctor => {
        if (String(doctor.sjid) === String(result.doctor_id)) {
          this.$set(doctor, 'ai_profile_tags', (result.tags || []).join('/'))
          this.$set(doctor, 'ai_profile_updated_at', result.profile_updated_at || '')
        }
      }))))
    },
    expandCreatedParent (level, parentId) {
      this.regions.forEach(region => (region.hospitals || []).forEach(hospital => {
        if (level === 'HOSPITAL' && region.sjid === parentId) this.$set(this.expanded.region, region.sjid, true)
        if (level === 'DEPARTMENT' && hospital.sjid === parentId) { this.$set(this.expanded.region, region.sjid, true); this.$set(this.expanded.hospital, hospital.sjid, true) }
        (hospital.departments || []).forEach(department => {
          if (level === 'DOCTOR' && department.sjid === parentId) { this.$set(this.expanded.region, region.sjid, true); this.$set(this.expanded.hospital, hospital.sjid, true); this.$set(this.expanded.department, department.sjid, true) }
        })
      }))
    },
    originLabel (value) { return value === 'AI' ? 'AI新增' : '手工新增' },
    maskMobile (value) { return value && value.length > 7 ? value.slice(0, value.length - 7) + '****' + value.slice(-4) : value }
  }
}
</script>

<style scoped>
.terminal-page{min-width:900px}.terminal-toolbar{display:flex;align-items:center;gap:10px;margin-bottom:18px;flex-wrap:wrap}.terminal-toolbar .el-input{width:300px}.primary-action{color:#fff!important;background:#168fd1!important;border-color:#168fd1!important}.ai-action{color:#fff;background:#7b52d2;border-color:#7b52d2}.ai-action:hover{background:#8b65dd;border-color:#8b65dd}.ai-note{color:#56647a;font-size:13px}.terminal-tree{padding:0 20px 16px}.tree-heading{height:56px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid var(--line);font-size:17px}.tree-heading strong{font-size:19px}.tree-heading span,.summary{color:#66748a}.summary{font-size:13px}.region-block{margin-top:10px}.tree-card{min-height:44px;box-sizing:border-box;border:1px solid #dfe5ef;border-radius:8px;background:#fff;display:flex;align-items:center;padding:7px 12px;transition:.2s}.tree-card:hover{border-color:#b9d9ea;box-shadow:0 4px 14px rgba(30,92,126,.08)}.node-toggle{cursor:pointer}.fold-arrow,.leaf-indent{width:20px;color:#71808d;font-size:15px;flex:none}.region-card{background:#fbfcff}.node-icon{width:28px;font-size:18px;flex:none}.node-main{min-width:0;flex:1;display:flex;align-items:center;gap:8px;white-space:nowrap}.node-main strong{font-size:16px;margin-right:0;max-width:45%;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.node-main .el-tag{flex:none}.node-main small{display:block;flex:1;min-width:0;color:#66748a;font-size:12px;line-height:20px;margin-top:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.node-count{flex:none;font-size:13px;color:#56647a;white-space:nowrap;margin-left:16px}.branch{position:relative;margin-top:6px}.branch:before{content:"";position:absolute;left:-18px;top:-10px;bottom:50%;border-left:2px solid #e1e7f0;border-bottom:2px solid #e1e7f0;width:18px}.hospital-branch{margin-left:38px}.department-branch{margin-left:42px}.doctor-branch{margin-left:42px}.hospital-card .el-tag{margin-left:4px;color:#168fd1;background:#eaf5fc;border:0}.level-tag{margin-left:6px;color:#7b52d2!important;background:#f1ebff!important;border:0}.ai-tag{margin-left:6px;color:#7b52d2!important;background:#f1ebff!important;border:0}.doctor-card{min-height:44px}.doctor-card .node-main strong{font-size:16px}.tree-fold-enter-active,.tree-fold-leave-active{transition:opacity .16s,transform .16s}.tree-fold-enter,.tree-fold-leave-to{opacity:0;transform:translateY(-4px)}.el-dialog .el-form-item{margin-bottom:18px}
@media(max-width:1200px){.terminal-page{min-width:760px}.ai-note{display:none}.doctor-branch,.department-branch{margin-left:28px}.hospital-branch{margin-left:28px}}
.ai-progress{color:#6042b5;font-size:13px;font-weight:600}.doctor-edit,.node-edit{margin-left:12px}.node-actions{display:flex;align-items:center;margin-left:12px;white-space:nowrap}.node-actions .el-button+.el-button{margin-left:10px}.tree-card .el-button{padding-top:4px;padding-bottom:4px}
</style>
